import { domainService } from '../../helpers/services/domain.service';
import i18n from '@/i18n'
import router from '@/router/index'
import { sharedHelper } from '@/utils'

export const state = {
  list: {
    data: [],
    pagination: {
      pageNumber: 0,
      pageSize: 0,
      lastPage: 0,
      tableRowsCount: 0
    }
  },
  details: {},
  results: {
    data: [],
    pagination: {
      pageNumber: 0,
      pageSize: 0,
      lastPage: 0,
      tableRowsCount: 0
    }
  },
  latestCheck: {},
  playerReports: {
    data: [],
    pagination: {
      pageNumber: 0,
      pageSize: 0,
      lastPage: 0,
      tableRowsCount: 0
    }
  },
  status: {}
}

export const DOMAIN_MODULE = "domain";

// define action
export const GET_ALL_DOMAINS = 'getAllDomains';
export const GET_DOMAIN = 'getDomain';
export const CREATE_DOMAIN = 'createDomain';
export const UPDATE_DOMAIN = 'updateDomain';
export const UPDATE_DOMAIN_STATUS = 'updateDomainStatus';
export const CHECK_DOMAIN = 'checkDomain';
export const GET_DOMAIN_RESULTS = 'getDomainResults';
export const GET_PLAYER_REPORTS = 'getPlayerReports';

// define mutation
const REQUESTING = "requesting";
const CHECK_REQUESTING = "checkRequesting";
const ALL_DOMAIN_SUCCESS = "allDomainSuccess";
const SUCCESS = "commonSuccess";
const FAILURE = "commonFailure";
const GET_DOMAIN_SUCCESS = "getDomainSuccess";
const CREATE_UPDATE_DOMAIN_SUCCESS = "createUpdateDomainSuccess";
const CHECK_DOMAIN_SUCCESS = "checkDomainSuccess";
const DOMAIN_RESULTS_SUCCESS = "domainResultsSuccess";
const PLAYER_REPORTS_SUCCESS = "playerReportsSuccess";

function mapDomain(x) {
  return {
    id: x.uuid,
    domain: x.domain,
    countryCode: x.country_code,
    remark: x.remark,
    isActive: x.is_active == 1,
    lastCheckedAt: x.last_checked_at,
    lastStatus: x.last_status,
    lastReason: x.last_reason,
    // Non-fatal findings from the last check (expiry, certificate) and a
    // status seen once by the scheduler but not yet confirmed.
    lastWarnings: x.last_warnings || [],
    pendingStatus: x.pending_status,
    problemDetectedAt: x.problem_detected_at,
    // Raw "could not open" count from players' browsers in the last window; informational only.
    recentFailures: x.recent_failures || null,
    domainGroupUuid: x.domain_group ? x.domain_group.uuid : null,
    domainGroupName: x.domain_group ? x.domain_group.name : null,
    domainGroupSlug: x.domain_group ? x.domain_group.slug : null,
    // Lower priority is handed out first by the group resolver.
    priority: x.priority != null ? x.priority : 100,
    ownerName: x.owner ? x.owner.name : null,
    ownerEmail: x.owner ? x.owner.email : null,
    createdAt: x.created_at,
    updatedAt: x.updated_at
  }
}

// One visit, as a real player's browser saw it.
function mapPlayerReport(x) {
  return {
    measuredAt: x.measured_at,
    domain: x.domain,
    asn: x.asn,
    networkName: x.network_name,
    isTelco: x.is_telco,
    networkType: x.network_type,
    opened: x.opened,
    timedOut: x.timed_out,
    elapsedMs: x.elapsed_ms,
    controlOpened: x.control_opened,
    ipPrefix: x.ip_prefix
  }
}

function mapCheckResult(x) {
  return {
    id: x.uuid,
    status: x.status,
    totalAsnCount: x.total_asn_count,
    blockedAsnCount: x.blocked_asn_count,
    details: x.details ? x.details.map((d) => {
      return {
        asn: d.asn,
        asnName: d.asn_name,
        blocked: d.blocked,
        verdict: d.verdict,
        // Probes that resolved through the telco's own DNS, and how many of
        // those saw the domain blocked.
        probeCount: d.probe_count,
        blockedProbeCount: d.blocked_probe_count,
        // Outcome of the follow-up sweep that bypasses the telco's resolver.
        control: d.control,
        // Live HTTPS fetch that ran because this telco's DNS looked blocked:
        // how many probes still opened the real site.
        http: d.http,
        httpProbeCount: d.http_probe_count,
        httpReachableCount: d.http_reachable_count,
        // Samples that reached the real site, samples that could not vote,
        // and the one line of evidence behind a blocked verdict.
        reachableCount: d.reachable_count,
        untrustedCount: d.untrusted_count,
        conclusive: d.conclusive,
        evidence: d.evidence,
        // Per network type: { fixed: { samples, blocked }, mobile: {...} }
        networks: d.networks || {},
        sources: d.sources || [],
        probes: d.probes || []
      }
    }) : null,
    // Findings of the registry / dns / site / browser layers.
    layers: x.layers || {},
    warnings: x.warnings || [],
    source: x.source,
    errorMessage: x.error_message,
    checkedAt: x.checked_at,
    telegramSent: x.telegram_sent,
    telegramError: x.telegram_error,
    createdAt: x.created_at
  }
}

export const actions = {

  // eslint-disable-next-line no-unused-vars
  [GET_ALL_DOMAINS]({ commit }, { filter }) {
    domainService
      .getAllDomains(filter)
      .then(
        (result) => {
          commit(ALL_DOMAIN_SUCCESS, { data: result })
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      )
  },

  // eslint-disable-next-line no-unused-vars
  [GET_DOMAIN]({ commit }, { uuid }) {
    domainService
      .getDomain(uuid)
      .then(
        (result) => {
          commit(GET_DOMAIN_SUCCESS, result)
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      );
  },

  // eslint-disable-next-line no-unused-vars
  [CREATE_DOMAIN]({ commit }, { data }) {
    commit(REQUESTING);
    domainService
      .createDomain(data)
      .then(
        // eslint-disable-next-line no-unused-vars
        (result) => {
          var message = i18n.t("message.createXSuccess", [i18n.t('module.domain')]);
          sharedHelper.alertSuccessMessage(message)
          commit(CREATE_UPDATE_DOMAIN_SUCCESS);
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      )
  },

  // eslint-disable-next-line no-unused-vars
  [UPDATE_DOMAIN]({ commit }, { data }) {
    commit(REQUESTING);
    domainService
      .updateDomain(data.id, data)
      .then(
        // eslint-disable-next-line no-unused-vars
        (result) => {
          var message = i18n.t("message.updateXSuccess", [i18n.t('module.domain')]);
          sharedHelper.alertSuccessMessage(message)
          commit(CREATE_UPDATE_DOMAIN_SUCCESS);
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      );
  },

  // eslint-disable-next-line no-unused-vars
  [UPDATE_DOMAIN_STATUS]({ dispatch, commit }, { filter, data }) {
    domainService.updateDomainStatus(data)
      .then(
        // eslint-disable-next-line no-unused-vars
        (result) => {
          var message = i18n.t('message.updateStatusSuccess');
          sharedHelper.alertSuccessMessage(message)
          dispatch(GET_ALL_DOMAINS, { filter })
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      )
  },

  // eslint-disable-next-line no-unused-vars
  [CHECK_DOMAIN]({ dispatch, commit }, { uuid, filter, resultsFilter }) {
    commit(CHECK_REQUESTING, uuid);
    domainService.checkDomain(uuid)
      .then(
        (result) => {
          commit(CHECK_DOMAIN_SUCCESS, result);
          var message = i18n.t('message.checkCompleted');
          sharedHelper.alertSuccessMessage(message)

          if (filter) {
            dispatch(GET_ALL_DOMAINS, { filter })
          }

          if (resultsFilter) {
            dispatch(GET_DOMAIN_RESULTS, { uuid, filter: resultsFilter })
          }
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      )
  },

  // eslint-disable-next-line no-unused-vars
  [GET_DOMAIN_RESULTS]({ commit }, { uuid, filter }) {
    domainService
      .getDomainResults(uuid, filter)
      .then(
        (result) => {
          commit(DOMAIN_RESULTS_SUCCESS, { data: result })
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      )
  },

  // eslint-disable-next-line no-unused-vars
  [GET_PLAYER_REPORTS]({ commit }, { uuid, filter }) {
    domainService
      .getPlayerReports(uuid, filter)
      .then(
        (result) => {
          commit(PLAYER_REPORTS_SUCCESS, { data: result })
        },
        (error) => {
          let message = sharedHelper.getErrorMessage(error);
          sharedHelper.alertErrorMessage(message, error.response.data.code)
          commit(FAILURE);
        }
      )
  },

};

export const mutations = {
  [ALL_DOMAIN_SUCCESS](state, { data }) {
    let d = data.data

    state.list.data = []
    state.list.pagination = sharedHelper.apiPaginationAdapter(data.pagination)

    if (data.success) {
      let list = []

      d.forEach((x) => {
        list.push(mapDomain(x))
      })

      state.list.data = list
    }
  },
  [SUCCESS](state) {
    state.status = {};
  },
  [FAILURE](state) {
    state.status = {};
  },
  [REQUESTING](state) {
    state.status = { processing: true };
  },
  [CHECK_REQUESTING](state, uuid) {
    state.status = { checking: uuid };
  },
  [CREATE_UPDATE_DOMAIN_SUCCESS](state) {
    state.status = {};
    router.push({ name: "Domain-listing" })
  },
  [GET_DOMAIN_SUCCESS](state, data) {
    state.details = mapDomain(data.data)
  },
  [CHECK_DOMAIN_SUCCESS](state, data) {
    state.status = {};
    state.latestCheck = mapCheckResult(data.data)
  },
  [DOMAIN_RESULTS_SUCCESS](state, { data }) {
    let d = data.data

    state.results.data = []
    state.results.pagination = sharedHelper.apiPaginationAdapter(data.pagination)

    if (data.success) {
      let list = []

      d.forEach((x) => {
        list.push(mapCheckResult(x))
      })

      state.results.data = list
    }
  },

  [PLAYER_REPORTS_SUCCESS](state, { data }) {
    let d = data.data

    state.playerReports.data = []
    state.playerReports.pagination = sharedHelper.apiPaginationAdapter(data.pagination)

    if (data.success) {
      let list = []

      d.forEach((x) => {
        list.push(mapPlayerReport(x))
      })

      state.playerReports.data = list
    }
  },
}
